import { create } from "zustand";
import { persist } from "zustand/middleware";
import { AdminAuthService } from "@/services/auth-service";

export interface Admin {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface AdminAuthState {
  admin: Admin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isRefreshing: boolean;
  logout: () => Promise<void>;
  ssoLogin: (accessToken: string) => Promise<void>;
  clearError: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setRefreshing: (refreshing: boolean) => void;
  fetchMe: () => Promise<void>;
  initAuth: () => Promise<void>;
}

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set, get) => ({
      admin: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isRefreshing: false,
      ssoLogin: async (accessToken: string) => {
        set({ isLoading: true, error: null });
        try {
          const authService = AdminAuthService();
          const res = await authService.adminSsoLogin(accessToken);
          const tokens = res.data.data;
          if (tokens) {
            localStorage.setItem("admin_access_token", tokens.access_token);
            localStorage.setItem("admin_refresh_token", tokens.refresh_token);
            set({ isAuthenticated: true, isLoading: false, error: null });
            await get().fetchMe();
          }
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Login failed. Please try again.";
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
          });
          throw error;
        }
      },
      fetchMe: async () => {
        try {
          const authService = AdminAuthService();
          const res = await authService.adminGetMe();
          const adminData = res.data.data;
          if (adminData) {
            set({ admin: adminData });
          }
        } catch {
          get().logout();
        }
      },
      initAuth: async () => {
        const token = localStorage.getItem("admin_access_token");
        if (!token) {
          set({ admin: null, isAuthenticated: false, isLoading: false });
          return;
        }
        set({ isLoading: true });
        try {
          const authService = AdminAuthService();
          const res = await authService.adminGetMe();
          const adminData = res.data.data;
          if (adminData) {
            set({ admin: adminData, isAuthenticated: true, isLoading: false });
          }
        } catch {
          localStorage.removeItem("admin_access_token");
          localStorage.removeItem("admin_refresh_token");
          set({ admin: null, isAuthenticated: false, isLoading: false });
        }
      },
      logout: async () => {
        const hasToken = !!localStorage.getItem("admin_access_token");
        try {
          if (hasToken) {
            await AdminAuthService().adminLogout();
          }
        } catch {
          // Ignore BE errors (expired token, network) — vẫn clear local
        } finally {
          localStorage.removeItem("admin_access_token");
          localStorage.removeItem("admin_refresh_token");
          set({ admin: null, isAuthenticated: false, error: null });
        }
      },
      clearError: () => set({ error: null }),
      setTokens: (accessToken: string, refreshToken: string) => {
        localStorage.setItem("admin_access_token", accessToken);
        localStorage.setItem("admin_refresh_token", refreshToken);
      },
      setRefreshing: (refreshing: boolean) => set({ isRefreshing: refreshing }),
    }),
    {
      name: "admin-auth-storage",
      partialize: (state) => ({
        admin: state.admin,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
